/**
 * Display rules for the student's "My Medical History" screen. A check-in comes
 * from GET /api/medical-check-ins/mine (student-safe fields only) and is shown
 * the way the coach's Medical Tracker shows it (components/dashboard/
 * MedicalTracker.js): the latest follow-up decides the Follow Up column, and so on.
 */

// Mirrors the symptom enum on the check-in model (models/medicalCheckIns.js).
export const SYMPTOM_OPTIONS = [
  { value: 'cough_cold', label: 'Cough + Cold' },
  { value: 'fever', label: 'Fever' },
  { value: 'stomach_ache', label: 'Stomach ache' },
  { value: 'headache', label: 'Headache' },
  { value: 'injury', label: 'Injury' },
  { value: 'other', label: 'Other' },
];
const SYMPTOM_LABEL = Object.fromEntries(SYMPTOM_OPTIONS.map(({ value, label }) => [value, label]));

export const STATUS_LABEL = { open: 'Open', under_treatment: 'Under Treatment', closed: 'Closed' };
export const STATUS_OPTIONS = Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }));

export const TIME_RANGES = [
  { value: 'all', label: 'All Time' },
  { value: '7d', label: 'Last 7 Days', days: 7 },
  { value: '30d', label: 'Last 30 Days', days: 30 },
  { value: '90d', label: 'Last 3 Months', days: 90 },
  { value: 'year', label: 'This Year' },
];

export const PAGE_SIZE = 10;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const asList = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);
const lastOf = (list) => (list.length > 0 ? list[list.length - 1] : null);
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// "10 Sep 2025", in the browser's own time zone (check-in times are entered locally).
// Built by hand rather than with toLocaleDateString, whose month abbreviations differ
// between browsers ("Sep" / "Sept").
export const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return '-';
  return `${String(date.getDate()).padStart(2, '0')} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

export const getStatus = (record) => (STATUS_LABEL[record?.status] ? record.status : 'open');

export const getSymptomsText = (record) => {
  const labels = asList(record.symptoms)
    .filter((symptom) => symptom !== 'other')
    .map((symptom) => SYMPTOM_LABEL[symptom] || symptom);
  if (record.customSymptom) labels.push(record.customSymptom);
  return labels.length > 0 ? labels.join(', ') : '-';
};

export const getMedicinesText = (record) => (record.medicines || '').trim() || '-';

// Every test noted across the visits, without repeats.
export const getTestsText = (record) => {
  const tests = asList(record.doctorVisits)
    .map((visit) => (visit.testDetails || '').trim())
    .filter(Boolean);
  return tests.length > 0 ? [...new Set(tests)].join(', ') : '-';
};

// The latest visit: its date, or failing that who it was with.
export const getDoctorVisitText = (record) => {
  const visit = lastOf(asList(record.doctorVisits));
  if (!visit) return '-';
  if (visit.visitDate) return formatDate(visit.visitDate);
  return visit.doctorName || visit.hospitalName || '-';
};

/**
 * The latest follow-up as { text, tone }. Tones: 'done' (completed), 'upcoming',
 * 'overdue' (its date has passed), 'active' (open with no date) and 'none'.
 */
export const getFollowUp = (record, now = new Date()) => {
  const followUp = lastOf(asList(record.followUps));
  if (!followUp) return { text: '-', tone: 'none' };
  if (followUp.status === 'completed') return { text: 'Completed', tone: 'done' };
  const due = toDate(followUp.followUpDate);
  if (due) {
    return {
      text: `Follow up on ${formatDate(due)}`,
      tone: due < startOfDay(now) ? 'overdue' : 'upcoming',
    };
  }
  if (followUp.status === 'active') return { text: 'Under treatment', tone: 'active' };
  return { text: '-', tone: 'none' };
};

export const matchesTimeRange = (record, range, now = new Date()) => {
  const option = TIME_RANGES.find((item) => item.value === range);
  if (!option || option.value === 'all') return true;
  const date = toDate(record.date);
  if (!date) return false;
  const today = startOfDay(now);
  const cutoff = option.days
    ? new Date(today.getFullYear(), today.getMonth(), today.getDate() - option.days)
    : new Date(today.getFullYear(), 0, 1);
  return date >= cutoff;
};

export const matchesSymptom = (record, symptom) => {
  if (symptom === 'all') return true;
  const symptoms = asList(record.symptoms);
  if (symptom === 'other') return symptoms.includes('other') || Boolean(record.customSymptom);
  return symptoms.includes(symptom);
};

export const applyFilters = (records, { time = 'all', symptom = 'all', status = 'all' } = {}, now = new Date()) =>
  records.filter(
    (record) =>
      matchesTimeRange(record, time, now) &&
      matchesSymptom(record, symptom) &&
      (status === 'all' || getStatus(record) === status)
  );

// Newest first ('desc') or oldest first ('asc'); records without a valid date go last.
export const sortByDate = (records, direction = 'desc') => {
  const time = (record) => toDate(record.date)?.getTime();
  return [...records].sort((a, b) => {
    const [ta, tb] = [time(a), time(b)];
    if (ta === undefined && tb === undefined) return 0;
    if (ta === undefined) return 1;
    if (tb === undefined) return -1;
    return direction === 'asc' ? ta - tb : tb - ta;
  });
};

// The three counts on the summary cards: every check-in, those where medicine
// was given, and those where a test was done.
export const summarize = (records) => ({
  total: records.length,
  treatments: records.filter((record) => getMedicinesText(record) !== '-').length,
  tests: records.filter((record) => getTestsText(record) !== '-').length,
});

/**
 * Page numbers to show, with 'gap' where some are skipped: all of them up to 7
 * pages, otherwise the first, the last and the current page with its neighbours.
 */
export const getPageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((page) => page >= 1 && page <= total).sort((a, b) => a - b);
  return sorted.flatMap((page, index) => (index > 0 && page - sorted[index - 1] > 1 ? ['gap', page] : [page]));
};
